import type { StructuredTargetVo } from 'models/milestones';
import { StructuredTargetType } from 'models/milestones';
import type { FilterBySportsCompetition, FilterBySportsItem } from 'models/search';
import { normalizeScopes } from 'queries/filterBySports';

/* -------------------------------------------------------------------------- */
/*                          NORMALIZATION                                     */
/* -------------------------------------------------------------------------- */

/**
 * Normalizes a string for comparison by:
 * - Converting to lowercase
 * - Removing spaces
 * - Removing non-alphanumeric characters
 *
 * Used for fuzzy matching of sports, competitions, scopes, and keywords.
 */
export const normalizeForComparison = (str: string): string => {
    return str
        .toLowerCase()
        .replace(/\s+/g, '')
        .replace(/[^a-z0-9]/gi, '');
};

/* -------------------------------------------------------------------------- */
/*                      MILESTONE → FILTER KEY                                */
/* -------------------------------------------------------------------------- */

/**
 * Maps milestone type labels to sport filter API keys where they differ.
 * E.g. milestoneTypeToLabel returns "Racing" but the filter API uses "Motorsport".
 */
const milestoneToFilterKey: Record<string, string> = {
    Racing: 'Motorsport',
};

/** Resolve the sport filter API key from a milestone label. */
export const sportFilterKey = (milestoneLabel: string): string =>
    milestoneToFilterKey[milestoneLabel] ?? milestoneLabel;

/* -------------------------------------------------------------------------- */
/*                          URL ENCODING                                      */
/* -------------------------------------------------------------------------- */

export const encodeSportFilter = (filter: string): string => {
    // Convert the filter to lowercase and replace spaces with hyphens
    return filter.toLowerCase().replace(/\s+/g, '-');
};

export const decodeSportFilter = (filter: string): string => {
    // Special case for 'all-sports'
    if (filter === 'all-sports') {
        return 'All sports';
    }

    // Replace hyphens with spaces and capitalize the first letter of each word
    return filter.replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

/** Lowercase, spaces→hyphens, strip non-alphanumeric. */
export const slugify = (text: string): string =>
    text
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/gi, '');

export const getSportsCompetitionUrl = (sport: string, competition: string, competitionScope?: string): string => {
    const baseUrl = `/category/sports/${encodeSportFilter(sportFilterKey(sport))}`;
    const competitionParam = slugify(competition);
    const competitionScopeParam = competitionScope ? `/${slugify(competitionScope)}` : '';
    return `${baseUrl}/${competitionParam}${competitionScopeParam}`;
};

/* -------------------------------------------------------------------------- */
/*                          SPORT MATCHING                                    */
/* -------------------------------------------------------------------------- */

const WORLD_CUP_COMPETITION_MATCH_KEY = 'fifaworldcup';
const WORLD_CUP_COMPETITION_ALIASES = new Set(['worldsoccercup', WORLD_CUP_COMPETITION_MATCH_KEY]);

const normalizeCompetitionForMatching = (competition: string): string => {
    const normalized = normalizeForComparison(competition);
    return WORLD_CUP_COMPETITION_ALIASES.has(normalized) ? WORLD_CUP_COMPETITION_MATCH_KEY : normalized;
};

export const matchSportsCompetition = (
    competitions: Record<string, FilterBySportsCompetition>,
    competition: string,
): string | undefined => {
    const possibleCompetitions = Object.keys(competitions || {});
    const normalizedInput = normalizeCompetitionForMatching(competition);

    const parsedCompetition = competition
        ? possibleCompetitions.find((comp) => normalizeCompetitionForMatching(comp) === normalizedInput)
        : undefined;

    const isValidCompetition = !!parsedCompetition && !!competitions[parsedCompetition];
    return isValidCompetition ? parsedCompetition : undefined;
};

export const matchSportsScope = (
    matchCompetition?: string,
    scope?: string,
    filtersBySportsItem?: FilterBySportsItem | undefined,
    scopeOrdering?: string[],
): string | undefined => {
    if (scope === 'props') {
        return 'Props';
    }

    const possibleScopes = normalizeScopes(
        !!matchCompetition
            ? filtersBySportsItem?.competitions?.[matchCompetition]?.scopes || []
            : filtersBySportsItem?.scopes || [],
        scopeOrdering,
    );

    const normalizedInput = scope ? normalizeForComparison(scope) : undefined;
    const parsedScope = normalizedInput
        ? possibleScopes.find((s) => normalizeForComparison(s) === normalizedInput)
        : undefined;

    return parsedScope ?? possibleScopes[0];
};

export const sportHasPropsScope = (sport: string): boolean => {
    const sportsWithPropsScope = ['All sports', 'Football', 'Basketball', 'Soccer', 'Hockey'];
    return sportsWithPropsScope.includes(sport);
};

export const competitionHasPropsScope = (competition: string): boolean => {
    const competitionsWithPropsScope = ['Pro Football', 'College Football', 'NHL'];
    return competitionsWithPropsScope.includes(competition);
};

const sportTeamTypes: Set<StructuredTargetType> = new Set([
    StructuredTargetType.SOCCER_TEAM,
    StructuredTargetType.BASKETBALL_TEAM,
    StructuredTargetType.BASEBALL_TEAM,
    StructuredTargetType.HOCKEY_TEAM,
    StructuredTargetType.CRICKET_TEAM,
    StructuredTargetType.FOOTBALL_TEAM,
    StructuredTargetType.UFC_COMPETITOR,
    StructuredTargetType.RUGBY_TEAM,
    StructuredTargetType.LACROSSE_TEAM,
]);

export const isSportTeam = (structuredTarget: StructuredTargetVo): boolean => {
    return sportTeamTypes.has(structuredTarget.type as StructuredTargetType);
};
